import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { supportedLanguages, languageNames, SupportedLanguage } from '../../i18n/config';
import { cn } from '../../utils/cn';

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact' | 'full';
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'default',
  className,
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = i18n.language as SupportedLanguage;
  const currentLangInfo = languageNames[currentLanguage] || languageNames.nl;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const switchLanguage = (locale: SupportedLanguage) => {
    i18n.changeLanguage(locale);
    setIsOpen(false);
  };

  if (variant === 'compact') {
    return (
      <div className={cn('relative', className)} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 p-2 text-gray-600 hover:text-primary rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Change language"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className="text-lg">{currentLangInfo.flag}</span>
          <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
        </button>

        {isOpen && (
          <div 
            className="absolute top-full right-0 mt-2 w-40 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50 animate-in fade-in slide-in-from-top-2"
            role="listbox"
          >
            {supportedLanguages.map((locale) => {
              const langInfo = languageNames[locale];
              const isActive = locale === currentLanguage;
              return (
                <button
                  key={locale}
                  onClick={() => switchLanguage(locale)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                  role="option"
                  aria-selected={isActive}
                >
                  <span className="text-lg">{langInfo.flag}</span>
                  <span className="flex-1 text-left">{langInfo.native}</span>
                  {isActive && <Check className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'full') {
    return (
      <div className={cn('flex gap-2', className)}>
        {supportedLanguages.map((locale) => {
          const langInfo = languageNames[locale];
          const isActive = locale === currentLanguage;
          return (
            <button
              key={locale}
              onClick={() => switchLanguage(locale)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg border transition-all',
                isActive
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              )}
              aria-pressed={isActive}
            >
              <span className="text-lg">{langInfo.flag}</span>
              <span>{langInfo.native}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // Default variant
  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-primary rounded-lg hover:bg-gray-50 transition-colors"
        aria-label="Change language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="w-5 h-5" />
        <span className="text-sm font-medium uppercase hidden sm:inline">
          {currentLanguage}
        </span>
        <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div 
          className="absolute top-full right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50 animate-in fade-in slide-in-from-top-2"
          role="listbox"
        >
          {supportedLanguages.map((locale) => {
            const langInfo = languageNames[locale];
            const isActive = locale === currentLanguage;
            return (
              <button
                key={locale}
                onClick={() => switchLanguage(locale)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-gray-700 hover:bg-secondary/10 hover:text-secondary'
                )}
                role="option"
                aria-selected={isActive}
              >
                <span className="text-lg">{langInfo.flag}</span>
                <span className="flex-1 text-left">{langInfo.native}</span>
                {isActive && <Check className="w-4 h-4" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
